// Survey options, profile types, and constants for ALINEA
// Language: Spanish coloquial adapted for low-literacy population

export const FEELINGS = {
    bien: { emoji: '🧘', label: 'En Eje', color: 'var(--color-bien)' },
    regular: { emoji: '⚖️', label: 'En Esfuerzo', color: 'var(--color-regular)' },
    mal: { emoji: '🌑', label: 'En Sombra', color: 'var(--color-mal)' },
};

/** Check-in somático (EPA — Pausas y recursos). Mismos valores API: bien | regular | mal. */
export const SOMATIC_CHECKIN_FEELINGS = {
    bien: { emoji: '🙂', label: 'Estoy bien', color: 'var(--color-bien)' },
    regular: { emoji: '💪', label: 'Estoy esforzándome', color: 'var(--color-regular)' },
    mal: { emoji: '🛑', label: 'Necesito parar', color: 'var(--color-mal)' },
    sick: { emoji: '🤒', label: 'Indispuesto', color: 'var(--color-regular)' },
};

export const FEELING_REASONS = {
    tension: { emoji: '🧱', label: 'Tensión en el Cuerpo (Cúmulo de carga)' },
    ruido: { emoji: '🌀', label: 'Ruido en la Cabeza (Muchas vueltas a lo mismo)' },
    foco: { emoji: '🌫️', label: 'Falta de Foco (Despiste o Olvido)' },
    bateria: { emoji: '🪫', label: 'Batería Baja (Cansancio total)' },
};

export const FATIGUE_OPTIONS = {
    no_bien: { emoji: '💪', label: 'Con Fuerza', color: 'var(--color-bien)' },
    un_poco: { emoji: '📈', label: 'Algo Cansado', color: 'var(--color-regular)' },
    muy_cansado: { emoji: '🛑', label: 'Agotado', color: 'var(--color-mal)' },
};

export const CLOSURE_OPTIONS = {
    valio_la_pena: { emoji: '✨', label: 'Valió la pena' },
    sin_ganas: { emoji: '📉', label: 'Sin energía' },
};

export const BOSS_PERCEPTION = {
    me_escucha: { emoji: '🧬', label: 'Me escucha y apoya' },
    lo_respeto: { emoji: '🤝', label: 'Buen respeto' },
    lo_aguanto: { emoji: '⛓️', label: 'Lo aguanto (Tensión)' },
};

export const TEAM_FEELING = {
    en_manada: { emoji: '🐺', label: 'Unidos (Manada)' },
    cada_uno: { emoji: '🚶', label: 'Cada uno por su lado' },
    tenso: { emoji: '⚡', label: 'Ambiente Tenso' },
};

export const DOMINANT_PHRASE = {
    y: { emoji: '🟢', label: '"Y" — Sumo y ayudo' },
    pero: { emoji: '🟡', label: '"Pero" — Siento frenos' },
    por_que: { emoji: '🔴', label: '"¿Por qué?" — No entiendo el problema' },
};

export const QUIT_THOUGHT = {
    no: { emoji: '😌', label: 'No' },
    a_veces: { emoji: '🤔', label: 'A veces' },
    si_mucho: { emoji: '😤', label: 'Sí, mucho' },
};

export const ALINEA_PROFILES = {
    constante: {
        v1_id: 'constante',
        v2_id: 'generacion',
        emoji: '🟢',
        name: 'El Constante (Ancla)',
        message: 'Mi fortaleza es la constancia.',
        description: 'Sostenedor del ritmo operativo. Tu energía es el motor que estabiliza la planta.',
        archetype: 'Generador de paz — ancla del ritmo',
        archetypeShort: 'Generador de paz — ancla del ritmo',
        inner_path: 'Ancla tu ritmo para sostener la unidad operativa.',
        ontology: 'La estabilidad como compromiso sagrado con el proceso maestro.',
        daily_anchor: 'Respira 3 veces antes de iniciar una tarea mecánica. Siente la solidez de tu aporte.'
    },
    impulsivo: {
        v1_id: 'impulsivo',
        v2_id: 'manifestacion',
        emoji: '🔴',
        name: 'El Impulsivo (Impacto)',
        message: 'Respiro antes de actuar.',
        description: 'Iniciador de cambios y fuerza de empuje. Tu decisión transforma los obstáculos en metas.',
        archetype: 'Manifestador de impacto',
        archetypeShort: 'Manifestador de impacto',
        inner_path: 'Canaliza tu fuego para liderar la maestría técnica.',
        ontology: 'De la reacción instintiva al impacto consciente y deliberado.',
        daily_anchor: 'Cuenta hasta 5 cuando sientas prisa. Transforma el impulso en dirección precisa.'
    },
    analitico: {
        v1_id: 'analitico',
        v2_id: 'proyeccion',
        emoji: '🔵',
        name: 'El Analítico (Claridad)',
        message: 'Confío en lo que sé.',
        description: 'Guía de precisión y calidad. Tu visión detecta la excelencia donde otros ven rutina.',
        archetype: 'Proyector de claridad',
        archetypeShort: 'Proyector de claridad',
        inner_path: 'Ilumina el procedimiento con tu rigor y perfección.',
        ontology: 'El conocimiento técnico como servicio de luz para toda la manada.',
        daily_anchor: 'Detecta un pequeño ajuste de calidad hoy. Comparte tu visión con un compañero.'
    },
    social: {
        v1_id: 'social',
        v2_id: 'reflejo',
        emoji: '🟡',
        name: 'El Social (Sintonía)',
        message: 'Cuido mi energía para nutrir al equipo.',
        description: 'Termómetro emocional de la manada. Tu presencia cohesiona y humaniza la operación.',
        archetype: 'Centro de sintonía',
        archetypeShort: 'Centro de sintonía',
        inner_path: 'Nutre los vínculos que blindan la resiliencia grupal.',
        ontology: 'El lenguaje como creador de mundos de confianza y pertenencia.',
        daily_anchor: 'Brinda un reconocimiento real hoy. Tu palabra es el pegamento de la excelencia.'
    },
};

export const ALERT_LEVELS = {
    red: { label: 'Alta', color: 'var(--color-alert-red)', emoji: '🔴' },
    yellow: { label: 'Media', color: 'var(--color-alert-yellow)', emoji: '🟡' },
    green: { label: 'Informativa', color: 'var(--color-alert-green)', emoji: '🟢' },
};

/** Texto breve para supervisor (columna manada). */
/** Ejes del radar de manada (orden: constante → impulsivo → analítico → social). */
export const RADAR_ARCHETYPE_AXES = [
    { key: 'constante', label: 'Ancla', color: '#16a34a' },
    { key: 'impulsivo', label: 'Impacto', color: '#dc2626' },
    { key: 'analitico', label: 'Claridad', color: '#2563eb' },
    { key: 'social', label: 'Sintonía', color: '#ca8a04' },
];

export const ARCHETYPE_SUPERVISOR_HINT = {
    constante: 'Sostiene el ritmo; prefiere estabilidad y constancia.',
    impulsivo: 'Arranque rápido; conviene acordar pausas antes de actuar.',
    analitico: 'Busca claridad y orden; detecta detalles del proceso.',
    social: 'Cuida el clima; conecta y cohesion al equipo.',
};

export const MANADA_ENERGY_HINT =
    'La energía es cómo llegó cada persona en su última llegada a jornada (el registro más reciente). No es un historial de fechas pasadas.';

export const MANADA_RHYTHM_HINT =
    'Ritmo natural: tendencia orientativa de cómo suelen moverse en el trabajo (para conversar, no para etiquetar).';

export const MANADA_ACT_HINT =
    'Formas de actuar: estilos habituales de iniciar tareas o responder al ritmo del turno.';

export const PROFILE_QUESTIONS = [
    {
        question: '¿Cómo prefiere trabajar?',
        options: [
            { value: 1, label: 'A mi ritmo, sin prisas' },
            { value: 2, label: 'Rápido, que se vea el avance' },
            { value: 3, label: 'Con calma, revisando cada detalle' },
            { value: 4, label: 'En equipo, platicando' },
        ],
    },
    {
        question: 'Cuando hay un problema, usted...',
        options: [
            { value: 1, label: 'Espero a que pase' },
            { value: 2, label: 'Lo enfrento de una vez' },
            { value: 3, label: 'Lo analizo con calma' },
            { value: 4, label: 'Busco quién me ayude' },
        ],
    },
    {
        question: '¿Qué es lo que más le gusta del trabajo?',
        options: [
            { value: 1, label: 'La rutina, saber qué esperar' },
            { value: 2, label: 'Los retos nuevos' },
            { value: 3, label: 'Aprender cómo funcionan las cosas' },
            { value: 4, label: 'Estar con los compañeros' },
        ],
    },
    {
        question: 'Cuando algo cambia en la planta, usted...',
        options: [
            { value: 1, label: 'Me adapto sin hacer ruido' },
            { value: 2, label: 'Me lanzo a probar lo nuevo' },
            { value: 3, label: 'Necesito entender por qué cambió' },
            { value: 4, label: 'Pregunto a los demás qué opinan' },
        ],
    },
    {
        question: 'Sus compañeros dirían que usted es...',
        options: [
            { value: 1, label: 'Confiable, siempre cumple' },
            { value: 2, label: 'El que le mete ganas' },
            { value: 3, label: 'El que nota todo' },
            { value: 4, label: 'El que anima al grupo' },
        ],
    },
    {
        question: 'Cuando se equivoca, usted...',
        options: [
            { value: 1, label: 'Lo corrijo callado' },
            { value: 2, label: 'Me enojo conmigo y sigo' },
            { value: 3, label: 'Reviso qué salió mal' },
            { value: 4, label: 'Le cuento a alguien de confianza' },
        ],
    },
    {
        question: '¿Qué le da más energía?',
        options: [
            { value: 1, label: 'Terminar lo que empecé' },
            { value: 2, label: 'Lograr algo difícil' },
            { value: 3, label: 'Entender algo nuevo' },
            { value: 4, label: 'Ayudar a un compañero' },
        ],
    },
    {
        question: 'Al final del día, ¿qué le da satisfacción?',
        options: [
            { value: 1, label: 'Que todo salió como siempre' },
            { value: 2, label: 'Que hice más de lo esperado' },
            { value: 3, label: 'Que aprendí algo' },
            { value: 4, label: 'Que el equipo estuvo bien' },
        ],
    },
    {
        question: 'Si pudiera elegir, trabajaría...',
        options: [
            { value: 1, label: 'Solo, tranquilo' },
            { value: 2, label: 'En lo más retador' },
            { value: 3, label: 'Donde pueda pensar' },
            { value: 4, label: 'Donde hay más gente' },
        ],
    },
    {
        question: 'Para usted, "labrar la maestría" es...',
        options: [
            { value: 1, label: 'Hacer bien lo mismo cada día' },
            { value: 2, label: 'Superar mis propios récords' },
            { value: 3, label: 'Conocer cada detalle del proceso' },
            { value: 4, label: 'Que todos crezcamos juntos' },
        ],
    },
];

export const CHANGE_TYPES = {
    nuevo_producto: { label: 'Nuevo producto', emoji: '📦' },
    nuevo_horario: { label: 'Nuevo horario', emoji: '🕐' },
    nuevo_jefe: { label: 'Nuevo jefe', emoji: '👤' },
    nuevo_proceso: { label: 'Nuevo proceso', emoji: '⚙️' },
    otro: { label: 'Otro', emoji: '📋' },
};

export const DECRETOS_CALIBRACION = {
    tension: "Tu cuerpo detecta una carga alostática. Respira, suelta los hombros y enfócate solo en el próximo gesto técnico. El movimiento consciente es tu medicina.",
    ruido: "Ese ruido mental es solo información, no es la verdad. Observa el pensamiento y déjalo pasar como el flujo de la línea. Vuelve a tu presencia aquí.",
    foco: "La niebla cognitiva es una señal de que necesitas micro-pausas. Enfoca tu mirada en un punto fijo por 10 segundos antes de seguir. Recupera tu eje.",
    bateria: "Tu batería está en reserva operativa. No fuerces la máquina. Prioriza lo vital y permite que tu ritmo natural dicte el paso hoy. Aceptación es poder.",
};

export const CIERRE_COSECHA = [
    "Maestría: Tu aporte de hoy es un ladrillo invisible en la catedral que estamos labrando juntos.",
    "Sentido: Lo que lograste hoy, por pequeño que parezca, alimenta la prosperidad de toda la manada.",
    "Propósito: Tu presencia y tu técnica hoy hicieron la diferencia. Ve a descansar con la consciencia del deber labrado.",
    "Legacy: Cada gesto técnico de hoy fue un acto de excelencia que honra tu naturaleza.",
];
